import React, { useState, useEffect } from "react";
import SlidingPane from "react-sliding-pane";
import MaskedInput from "react-text-mask";
import createNumberMask from "text-mask-addons/dist/createNumberMask";
import client from "../../../shared/api/client";
import { toastSuccess, toastError } from "../../../shared/toastHelper";
import XCloseIcon from "../../shared/Icons/XCloseIcon";
import TrashIcon from "../../shared/Icons/TrashIcon";

const STAGE_TYPES = [
  "Auditorium", "Bar Stage", "Black Box", "Custom", "Lounge",
  "Main Stage", "Outdoor Stage", "Rooftop", "Studio", "Theater"
];

const capacityMask = createNumberMask({
  prefix: "",
  suffix: "",
  includeThousandsSeparator: true,
  thousandsSeparatorSymbol: ",",
  allowDecimal: false,
  allowNegative: false,
  allowLeadingZeroes: false,
  integerLimit: 7
});

const StageFormModal = ({
  isOpen,
  onClose,
  team,
  venueOwnership,
  room,
  onStageChanged,
}) => {
  const isEditMode = !!room;

  const [name, setName] = useState("");
  const [capacity, setCapacity] = useState("");
  const [stageType, setStageType] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (isEditMode) {
      setName(room.name || "");
      setCapacity(String(room.capacity || ""));
      setStageType(room.stage_type || "");
      setNotes(room.notes || "");
    } else {
      setName("");
      setCapacity("");
      setStageType("");
      setNotes("");
    }
    setSubmitting(false);
  }, [isOpen]);

  const canSubmit =
    name.trim().length > 0 &&
    capacity &&
    String(capacity).replace(/,/g, "").length > 0 &&
    stageType;

  const isLastStage = isEditMode && venueOwnership && (venueOwnership.rooms || []).length <= 1;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);

    const roomParams = {
      room: {
        name: name.trim(),
        capacity: String(capacity).replace(/,/g, ""),
        stage_type: stageType,
        notes: notes,
        venue_ownership_id: venueOwnership.id,
      },
    };

    try {
      if (isEditMode) {
        const res = await client.patch(
          `/teams/${team.id}/rooms/${room.id}`,
          roomParams
        );
        const updatedRooms = (venueOwnership.rooms || []).map((r) =>
          r.id === room.id ? { id: res.data.id, name: res.data.name } : r
        );
        onStageChanged({ ...venueOwnership, rooms: updatedRooms });
        toastSuccess("Stage updated successfully.");
      } else {
        const res = await client.post(
          `/teams/${team.id}/rooms`,
          roomParams
        );
        const updatedRooms = [
          ...(venueOwnership.rooms || []),
          { id: res.data.id, name: res.data.name },
        ];
        onStageChanged({ ...venueOwnership, rooms: updatedRooms });
        toastSuccess("Stage created successfully.");
      }
      onClose();
    } catch (error) {
      var message = "Something went wrong. Please try again.";
      if (error.response && error.response.data) {
        var errors = error.response.data;
        if (Array.isArray(errors) && errors.length > 0) {
          message = errors.join(", ");
        }
      }
      toastError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to remove "${name}"? This action cannot be undone.`)) {
      return;
    }

    setSubmitting(true);
    try {
      await client.delete(`/teams/${team.id}/rooms/${room.id}`);
      const updatedRooms = (venueOwnership.rooms || []).filter(
        (r) => r.id !== room.id
      );
      onStageChanged({ ...venueOwnership, rooms: updatedRooms });
      toastSuccess("Stage removed successfully.");
      onClose();
    } catch (error) {
      var message = "Something went wrong. Please try again.";
      if (error.response && error.response.data) {
        var errors = error.response.data;
        if (Array.isArray(errors) && errors.length > 0) {
          message = errors.join(", ");
        }
      }
      toastError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SlidingPane
      isOpen={isOpen}
      className="sliding-pane-container stage-form-pane"
      overlayClassName="stage-form-pane-overlay"
      width={"400px"}
      hideHeader={true}
      onRequestClose={onClose}
    >
      <style>{`
        /* Softer, design-system shadow instead of the library default
           0 8px 8px rgba(0,0,0,0.5); scoped to this pane only. */
        .stage-form-pane.slide-pane {
          box-shadow: 0px 12px 16px -4px rgba(10, 13, 18, 0.08),
                      0px 4px 6px -2px rgba(10, 13, 18, 0.03),
                      0px 2px 2px -1px rgba(10, 13, 18, 0.04);
        }
        /* No page-dimming overlay — match the Calendar pane UX. Scoped here. */
        .stage-form-pane-overlay.slide-pane__overlay,
        .stage-form-pane-overlay.ReactModal__Overlay--after-open {
          background-color: transparent !important;
        }
        html[data-theme="dark"] body.od-themed .stage-form-pane.slide-pane,
        html[data-theme="dark"] body.od-themed .stage-form-pane .slide-pane__content {
          background-color: var(--sl-bg-primary);
          color: var(--sl-text-primary);
        }
        html[data-theme="dark"] body.od-themed .stage-form-pane .form-control,
        html[data-theme="dark"] body.od-themed .stage-form-pane .custom-select {
          background-color: var(--sl-bg-primary);
          color: var(--sl-text-primary);
        }
        html[data-theme="dark"] body.od-themed .stage-form-pane .form-control::placeholder {
          color: var(--sl-text-placeholder);
        }
        /* Setlist single chevron-down instead of the native OS double-arrow. */
        .stage-form-pane .custom-select {
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23717680' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          background-size: 12px 8px;
          padding-right: 32px;
        }
        html[data-theme="dark"] body.od-themed .stage-form-pane .custom-select {
          background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23A4ABB8' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
        }
      `}</style>
      <div className="row">
        <div className="col-12">
          <div className="row mx-0"
               style={{ borderBottom: "2px solid var(--sl-border-secondary, #f5f5f5)" }}>
            <div className="col" style={{ padding: "20px 24px" }}>
              <strong style={{ fontSize: "18px" }}>
                {isEditMode ? "Edit Stage" : "Add Stage"}
              </strong>
            </div>
            <div className="col-xs-auto d-flex align-items-center" style={{ paddingRight: "15px" }}>
              <a href="#"
                 style={{ fontSize: "24px", color: "var(--sl-text-primary, #181d27)" }}
                 onClick={(e) => {
                   e.preventDefault();
                   onClose();
                 }}>
                <XCloseIcon width="20" height="20" />
              </a>
            </div>
          </div>
          <div className="row mx-0">
            <div className="col-12" style={{ padding: "16px" }}>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="stage-name" style={{ fontSize: "14px" }}>
                    Stage Name
                    <span style={{ color: "var(--sl-text-brand, #136395)" }}>*</span>
                  </label>
                  <input type="text"
                         className="form-control"
                         id="stage-name"
                         placeholder="Enter stage name"
                         value={name}
                         onChange={(e) => setName(e.target.value)}
                         style={{ border: "1px solid var(--sl-border-primary, #e6e6e6)", fontSize: "16px" }} />
                </div>
                <div className="form-group">
                  <label htmlFor="stage-capacity" style={{ fontSize: "14px" }}>
                    Capacity
                    <span style={{ color: "var(--sl-text-brand, #136395)" }}>*</span>
                  </label>
                  <MaskedInput
                    className="form-control"
                    id="stage-capacity"
                    placeholder="Enter a capacity"
                    mask={capacityMask}
                    value={capacity}
                    onChange={(e) => setCapacity(e.target.value)}
                    guide={false}
                    style={{ border: "1px solid var(--sl-border-primary, #e6e6e6)", fontSize: "16px" }} />
                </div>
                <div className="form-group">
                  <label htmlFor="stage-type" style={{ fontSize: "14px" }}>
                    Stage Type
                    <span style={{ color: "var(--sl-text-brand, #136395)" }}>*</span>
                  </label>
                  <select
                    className="form-control custom-select"
                    id="stage-type"
                    value={stageType}
                    onChange={(e) => setStageType(e.target.value)}
                    style={{ border: "1px solid var(--sl-border-primary, #e6e6e6)", fontSize: "16px" }}>
                    <option value="">Select stage type</option>
                    {STAGE_TYPES.map((st) => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="stage-notes" style={{ fontSize: "14px" }}>
                    Note
                  </label>
                  <textarea
                    className="form-control"
                    id="stage-notes"
                    placeholder="Add a note"
                    rows="3"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    style={{ border: "1px solid var(--sl-border-primary, #e6e6e6)", fontSize: "16px" }} />
                </div>
                <div className={"form-group d-flex align-items-center " + (isEditMode ? "justify-content-between" : "justify-content-end")}>
                  {isEditMode && !isLastStage ? (
                    <a href="#"
                       onClick={(e) => {
                         e.preventDefault();
                         handleDelete();
                       }}
                       style={{ fontWeight: "600", display: "inline-flex", alignItems: "center", gap: "5px" }}
                       className="text-danger">
                      <TrashIcon width="16" height="16" />
                      Remove Stage
                    </a>
                  ) : null}
                  <div>
                    <a href="#"
                       onClick={(e) => {
                         e.preventDefault();
                         onClose();
                       }}
                       style={{
                         marginRight: "10px",
                         fontWeight: "600",
                         border: "1px solid var(--sl-border-primary, #D5D7DA)"
                       }}
                       className="btn btn-outline-secondary">
                      Cancel
                    </a>
                    <button type="submit"
                            disabled={!canSubmit || submitting}
                            style={{ fontWeight: "600" }}
                            className="btn btn-primary">
                      {submitting
                        ? "Saving..."
                        : isEditMode
                        ? "Save"
                        : "Create Stage"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </SlidingPane>
  );
};

export default StageFormModal;
